import React, { useEffect, useMemo, useState } from "react";
import { api, fmt } from "../../../services/erpApi";
import { StatCard, PageHeader, LoadingScreen } from "../../../components/admin/EssUi";
import { Search, ScrollText, Banknote, Hourglass, AlertTriangle } from "lucide-react";

const PAGE_SIZE = 15;

const BUCKETS = ["current", "0-30", "31-60", "61-90", "90+"];

const bucketOrder = (b) => (b === "current" ? -1 : Number(b.split("-")[0]));

const AdminPanelAgingPage = () => {
  const [tab, setTab] = useState("receivables");
  const [data, setData] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [search, setSearch] = useState("");
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [bucketFilter, setBucketFilter] = useState("all");
  const [page, setPage] = useState(1);

  useEffect(() => { setPage(1); }, [tab, search, overdueOnly, bucketFilter]);

  const load = async () => {
    try {
      const params = { limit: 500 };
      if (overdueOnly) params.overdue = true;
      const rows = tab === "receivables"
        ? await api.accounting.receivables(params)
        : await api.accounting.payables(params);
      setData(rows || []);
    } catch { setData([]); }
  };

  useEffect(() => {
    (async () => {
      await load();
      setLoaded(true);
    })();
  }, []);

  useEffect(() => {
    if (loaded) load();
  }, [tab, overdueOnly]);

  const nameStatusNoState = (row) => {
    const due = new Date(row.date_due);
    if (isNaN(due)) return "open";
    return Math.round((new Date() - due) / 86400000) > 0 ? "overdue" : "open";
  };

  const stats = useMemo(() => {
    const total = data.reduce((s, r) => s + (Number(r.amount_total) || 0), 0);
    const outstanding = data.reduce((s, r) => s + (Number(r.amount_residual) || 0), 0);
    const overdueCount = data.filter((r) => nameStatusNoState(r) === "overdue").length;
    const overdueAmt = data.filter((r) => nameStatusNoState(r) === "overdue").reduce((s, r) => s + (Number(r.amount_residual) || 0), 0);
    return { count: data.length, total, outstanding, overdueCount, overdueAmt };
  }, [data]);

  const nameStatus = (row) => {
    const due = new Date(row.date_due);
    if (isNaN(due)) return "open";
    const daysAgo = Math.round((new Date() - due) / 86400000);
    return daysAgo > 0 ? "overdue" : daysAgo === 0 ? "due" : "open";
  };

  const effectiveBucket = (row) => row.aging_bucket || (nameStatus(row) === "overdue" ? "0-30" : "current");

  const bucketTotals = useMemo(() => {
    const map = {};
    BUCKETS.forEach((b) => { map[b] = { count: 0, residual: 0 }; });
    data.forEach((r) => {
      const b = effectiveBucket(r);
      if (map[b]) { map[b].count += 1; map[b].residual += Number(r.amount_residual) || 0; }
    });
    return map;
  }, [data]);

  const filtered = useMemo(() => {
    return data.filter((r) => {
      if (bucketFilter !== "all" && effectiveBucket(r) !== bucketFilter) return false;
      if (search.trim()) {
        const q = search.toLowerCase();
        const hay = `${r.invoice_name || r.bill_name || ""} ${r.partner_name || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [data, search, bucketFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const revenue = tab === "receivables";

  const inputStyle = {
    width: "100%", padding: "9px 12px", border: "1px solid #cbd5e1", borderRadius: "6px",
    fontSize: "13.5px", outline: "none", background: "#fff", boxSizing: "border-box",
  };

  if (!loaded) return <LoadingScreen />;

  return (
    <div className="max-w-7xl mx-auto animate-fade-in" style={{ padding: "0 16px" }}>
      <PageHeader
        title="Receivables & Payables"
        subtitle="Track open customer receivables and supplier payables by due date"
      />

      <div className="ess-sg ess-sg4" style={{ marginBottom: "24px" }}>
        <StatCard label={revenue ? "Open Receivables" : "Open Payables"} value={String(stats.count)} icon={ScrollText} color="indigo" trend={`${revenue ? "Customer" : "Supplier"} open items`} />
        <StatCard label="Total" value={fmt.moneyShort(stats.total)} icon={Banknote} color="slate" trend={fmt.money(stats.total)} />
        <StatCard label="Outstanding" value={fmt.moneyShort(stats.outstanding)} icon={Hourglass} color="amber" trend={fmt.money(stats.outstanding)} />
        <StatCard label="Overdue" value={fmt.moneyShort(stats.overdueAmt)} icon={AlertTriangle} color="rose" trend={`${stats.overdueCount} items past due`} />
      </div>

      <div style={{ display: "flex", gap: "24px", marginBottom: "20px", borderBottom: "1px solid #e2e7e2", overflowX: "auto" }}>
        {[
          { key: "receivables", label: "Receivables (Customer)" },
          { key: "payables", label: "Payables (Vendor)" },
        ].map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            padding: "9px 2px", border: "none", background: "transparent",
            borderBottom: `2px solid ${tab === t.key ? "#225f2b" : "transparent"}`,
            marginBottom: -1, whiteSpace: "nowrap",
            color: tab === t.key ? "#225f2b" : "#64748b", fontSize: "13px",
            fontWeight: tab === t.key ? "600" : "500", cursor: "pointer",
            display: "flex", alignItems: "center", gap: "6px"
          }}>
            {t.label}
          </button>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: "10px", marginBottom: "20px" }}>
        {BUCKETS.map((b) => {
          const t = bucketTotals[b];
          const active = bucketFilter === b;
          const late = b !== "current";
          return (
            <button
              key={b}
              onClick={() => setBucketFilter(active ? "all" : b)}
              style={{
                display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "2px",
                padding: "12px 14px", borderRadius: "12px", cursor: "pointer", textAlign: "left",
                border: active ? "2px solid #225f2b" : "1px solid #e2e7e2",
                background: active ? "#f0f7f0" : "white", transition: "all 0.15s",
              }}
            >
              <span style={{ fontSize: "12px", fontWeight: "700", color: late ? "#e11d48" : "#0891b2", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                {b === "current" ? "Current" : `${b} days`}
              </span>
              <span style={{ fontSize: "16px", fontWeight: "800", color: "#1e293b" }}>{fmt.moneyShort(t.residual)}</span>
              <span style={{ fontSize: "12px", color: "#64748b" }}>{t.count} {tab === "receivables" ? "invoices" : "bills"}</span>
            </button>
          );
        })}
      </div>

      <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", marginBottom: "24px", background: "white", padding: "16px", borderRadius: "8px", border: "1px solid #e2e7e2", alignItems: "center" }}>
        <div style={{ flex: 1, minWidth: "200px", position: "relative" }}>
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "#94a3b8" }} />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={`Search ${revenue ? "invoice" : "bill"} or partner...`} style={{ ...inputStyle, padding: "10px 12px 10px 36px" }} />
        </div>
        <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", fontWeight: "500", color: "#475569", cursor: "pointer" }}>
          <input type="checkbox" checked={overdueOnly} onChange={(e) => setOverdueOnly(e.target.checked)} style={{ width: "16px", height: "16px" }} />
          Overdue only
        </label>
      </div>

      <div style={{ background: "white", borderRadius: "8px", border: "1px solid #e2e7e2", overflow: "hidden" }}>
        {paginated.length === 0 ? (
          <div className="ess-empty">No {revenue ? "receivables" : "payables"}.</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
              <thead>
                <tr>
                  <th className="ess-th" style={{ minWidth: "150px" }}>{revenue ? "Invoice" : "Bill"}</th>
                  <th className="ess-th" style={{ minWidth: "170px" }}>{revenue ? "Customer" : "Vendor"}</th>
                  <th className="ess-th" style={{ minWidth: "110px" }}>Date</th>
                  <th className="ess-th" style={{ minWidth: "110px" }}>Due</th>
                  <th className="ess-th ess-th-r" style={{ minWidth: "120px" }}>Amount</th>
                  <th className="ess-th ess-th-r" style={{ minWidth: "120px" }}>Residual</th>
                  <th className="ess-th ess-th-r" style={{ minWidth: "90px" }}>Overdue</th>
                  <th className="ess-th" style={{ minWidth: "90px" }}>Bucket</th>
                  <th className="ess-th" style={{ minWidth: "90px" }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {paginated.map((row, index) => {
                  const st = nameStatus(row);
                  const overdue = st === "overdue";
                  const days = Math.round((new Date() - new Date(row.date_due)) / 86400000);
                  return (
                    <tr key={row.invoice_id || row.supplier_bill_id || row.id || index} className="ess-tr">
                      <td className="ess-td" style={{ fontWeight: "600", color: "#225f2b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{row.invoice_name || row.bill_name || "—"}</td>
                      <td className="ess-td" style={{ fontWeight: "500", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{row.partner_name || "—"}</td>
                      <td className="ess-td" style={{ color: "#64748b", whiteSpace: "nowrap" }}>{fmt.date(row.date_invoice)}</td>
                      <td className="ess-td" style={{ color: "#475569", whiteSpace: "nowrap" }}>{fmt.date(row.date_due)}</td>
                      <td className="ess-td" style={{ fontWeight: "600", textAlign: "right", whiteSpace: "nowrap" }}>{fmt.money(row.amount_total)}</td>
                      <td className="ess-td" style={{ fontWeight: "600", color: overdue ? "#be123c" : "#1e293b", textAlign: "right", whiteSpace: "nowrap" }}>{fmt.money(row.amount_residual)}</td>
                      <td className="ess-td" style={{ color: overdue ? "#be123c" : "#94a3b8", textAlign: "right", fontWeight: "600", whiteSpace: "nowrap" }}>
                        {typeof row.days_overdue === "number" ? (row.days_overdue > 0 ? `${row.days_overdue}d` : "—") : (overdue ? `${days}d` : "—")}
                      </td>
                      <td className="ess-td" style={{ whiteSpace: "nowrap" }}>
                        <span style={{ background: effectiveBucket(row) === "current" ? "#f0f7f0" : "#fffbeb", color: effectiveBucket(row) === "current" ? "#225f2b" : "#b45309", padding: "3px 10px", borderRadius: "6px", fontSize: "12px", fontWeight: "600", whiteSpace: "nowrap" }}>
                          {effectiveBucket(row) === "current" ? "Current" : `${effectiveBucket(row)}d`}
                        </span>
                      </td>
                      <td className="ess-td" style={{ whiteSpace: "nowrap" }}>
                        <span style={{ background: overdue ? "#fff1f2" : "#f1f5f9", color: overdue ? "#be123c" : "#475569", padding: "3px 10px", borderRadius: "6px", fontSize: "12px", fontWeight: "600", whiteSpace: "nowrap" }}>
                          {overdue ? `${days} day${days === 1 ? "" : "s"} overdue` : st === "due" ? "Due today" : "Open"}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", borderTop: "1px solid #e2e7e2", background: "#fafafa" }}>
          <span style={{ fontSize: "13px", color: "#64748b" }}>
            Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length}
          </span>
          <div style={{ display: "flex", gap: "4px" }}>
            <button disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="ess-btn ess-btn-secondary">← Prev</button>
            <button disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} className="ess-btn ess-btn-secondary">Next →</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPanelAgingPage;